// Auto-record track logs on takeoff (and optionally stop them on
// landing). Driven by the airborne detector.
//
// Setting lives in /settings as `autoRecordTrackOnTakeoff` and the
// matching `autoStopTrackOnLanding`. This module is a thin glue:
// it subscribes to airborne transitions and calls trackStore.start()
// / stop() at the right edges. We never start a track when one is
// already recording.
//
// Cold-start case (added 2026-05-12): the airborne detector's
// onAirborneChange() fires `(state, state)` once on subscribe so
// listeners know the current state. The takeoff/landing edge
// detector skips that bootstrap. But if Fyrefly is opened MID-FLIGHT
// (cold load on the iPad after a tab eviction) we want to start
// a track immediately when the detector confirms airborne, since
// we missed the actual takeoff edge.

import { onAirborneChange } from './airborneDetector';
import { useTrackStore } from '../stores/trackStore';
import { useUiStore } from '../stores/uiStore';

let mounted = false;
let unsub: (() => void) | null = null;
/** Whether we've already acted on the initial bootstrap state. We only
 *  honour the bootstrap once — subsequent listener fires are edge
 *  transitions handled by the normal path. */
let bootstrapHandled = false;

export function startAutoTrack(): void {
  if (mounted) return;
  mounted = true;
  bootstrapHandled = false;
  unsub = onAirborneChange((next, prev) => {
    const ui = useUiStore.getState();
    const track = useTrackStore.getState();

    // Bootstrap: first fire delivers (state, state). If we're already
    // airborne at app load (cold-start mid-flight) and the user opts in,
    // start a track. After this fire we revert to pure edge handling.
    if (next === prev) {
      if (!bootstrapHandled) {
        bootstrapHandled = true;
        if (next === 'airborne' && ui.autoRecordTrackOnTakeoff && !track.recording) {
          track.start();
        }
      }
      return;
    }

    // Normal edge case: takeoff / landing transitions.
    if (next === 'airborne' && ui.autoRecordTrackOnTakeoff && !track.recording) {
      track.start();
    } else if (prev === 'airborne' && next !== 'airborne'
               && ui.autoStopTrackOnLanding && track.recording) {
      track.stop();
    }
  });
}

export function stopAutoTrack(): void {
  if (unsub) { unsub(); unsub = null; }
  mounted = false;
}
